/** @module Classes */ /** typedoc */
import { keys } from 'ts-transformer-keys';

import {
    GenresEnum,
    ReleaseContributorRoleEnum,
    ReleaseGlobalStateEnum,
    ReleaseLanguagesEnum,
    ReleaseStateEnum,
    ReleaseStoreCategoryEnum,
    ReleaseStoreEnum,
    ReleaseTerritoriesEnum,
    MonetizationEligibilityStateEnum,
    ServiceProviderStateEnum,
} from '../../enums';

export class ReleaseArtist {
    name: string = null;
    id: string = null;
}

export class ReleaseSongwriter extends ReleaseArtist {
    publisherName: string = null;
}

export class ReleaseLyricist extends ReleaseArtist {
    publisherName: string = null;
}

export class ReleaseContributor {
    role: ReleaseContributorRoleEnum = null;
    artists: Array<ReleaseArtist> = [];
}

export class ReleasePublisher extends ReleaseArtist {
    email: string = null;
    publishingHouse: string = null;
}

export class Release {
    additionalArtists: Array<ReleaseArtist> = [];
    autoGenerateLabel = true;
    autoGenerateUpcCode = true;
    catalogNumber: string = null;
    compositionOwner: string = null;
    compositionYear: number = null;
    countries: Array<ReleaseTerritoriesEnum> = [];
    errors?: Array<ReleaseError>;
    excludeCountries = false;
    expirationDate: Date = null;
    globalState: ReleaseGlobalStateEnum = null;
    hasCoverArt = false;
    hasCoverSong = false;
    hasOriginalReleaseDate = false;
    label: string = null;
    language: ReleaseLanguagesEnum = null;
    lastUpdateTime: string = null;
    mainGenre: GenresEnum = null;
    mainSubGenre: GenresEnum = null;
    masterRecordingOwner: string = null;
    masterRecordingYear: number = null;
    originalReleaseDate: string = null;
    parentalAdvisory = false;
    primaryArtists: Array<ReleaseArtist> = [];
    progress: number = null;
    releaseDate: string = null;
    releaseFormat: string = null;
    releaseTargetDate: string = null;
    restrictCountries = false;
    serviceProviders: Array<{name: string}> = [];
    signedToken: {token: string, signature: string} = null;
    state: ReleaseStateEnum = null;
    supplier: string = null;
    tracks: Array<ReleaseTrack> = [];
    upcCode: string = null;
    userId: string = null;
    monetizationEligibilityState: MonetizationEligibilityStateEnum = null;

    constructor(
        public id: string,
        public name: string,
        public creationTime: string
    ) {}
}

export const keysOfRelease: Array<keyof Release> = keys<Release>();

export class ReleaseTrack {
    originalTrackId: string;
    alreadyReleased: ReusedTrackInfos = null;
    genre: GenresEnum = null;
    subGenre: GenresEnum = null;
    previewStart = 0;
    previewLength = 30;
    primaryArtists: Array<ReleaseArtist> = [];
    additionalArtists: Array<ReleaseArtist> = [];
    publishers: Array<ReleasePublisher> = [];
    producers: Array<ReleaseArtist> = [];
    contributors: Array<ReleaseContributor> = [];
    autoGenerateIsrcCode = true;
    isrcCode: string = null;
    parentalAdvisory = false;
    availableSeparately = true;
    language: ReleaseLanguagesEnum = null;
    metadata: {[k: string]: string} = null;
    isCoverSong = false;
    coverInfos: ReleaseTrackCoverInfos = {
        originalTrackTitle: null,
        originalArtistsNames: [],
        originalSongwriters: [],
        originalTrackLink: null,
    };
    songwriters: Array<ReleaseSongwriter> = [];
    lyricists: Array<ReleaseLyricist> = [];
    hasLyrics: boolean = null;
    lyricsLanguage: string = null;

    constructor(
        public id: string,
        public name: string
    ) {}
}

export class ReusedTrackInfos {
    releaseId: string = null;
    unlockedFields: Array<keyof ReleaseTrack> = [];
    upcCode: string = null;
}

/**
 * State of an specific Service Provider (DSP)
 */
export class ReleaseServiceProviderState {
    name: ReleaseStoreEnum = null;
    category: ReleaseStoreCategoryEnum = null;
    state: ServiceProviderStateEnum = null;
}

export class ReleaseTrackCoverInfos {
    originalTrackTitle: string = null;
    originalArtistsNames: Array<ReleaseArtist> = [];
    originalSongwriters: Array<ReleaseSongwriter> = [];
    originalTrackLink: string = null;
}

export class ReleaseStore {
    isAvailable = true;
    category: ReleaseStoreCategoryEnum = null;
    error: ReleaseError = null;

    constructor(
        public name: ReleaseStoreEnum = null,
        public usersCount = 0,
        public hasLogo = false
    ) {}
}

export class ReleaseError {
    constructor(
        public code: string,
        public message: string
    ) {}
}

export class ReleaseTrend {
    constructor(
        public value: string,
        public count: number
    ) {}
}

export class ReleaseTrendsDailyTotal {
    date: string = null;
    downloads: number = null;
    streams: number = null;
}
